import React, { useEffect, useRef, useState } from 'react';

const MessengerTrackingMap = ({ routePoints, height = '500px', selectedPointId }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef({});
    const [L, setL] = useState(null);

    useEffect(() => {
        // Carga dinámica de Leaflet
        const initLeaflet = async () => {
            const leaflet = await import('leaflet');
            await import('leaflet/dist/leaflet.css');
            setL(leaflet);
        };
        initLeaflet();
    }, []);

    useEffect(() => {
        if (!L || !mapContainerRef.current || mapInstanceRef.current) return;

        // Configurar icono por defecto corregido para Leaflet en React
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        });

        // Inicializar mapa centrado en Bogotá (centro de operaciones)
        const map = L.map(mapContainerRef.current).setView([4.6097, -74.0817], 12);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors'
        }).addTo(map);

        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [L]);

    useEffect(() => {
        if (!L || !mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // Limpiar capas previas si las hay (excepto el tileLayer)
        map.eachLayer((layer) => {
            if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.CircleMarker) {
                map.removeLayer(layer);
            }
        });

        if (!routePoints || routePoints.length === 0) return;

        const latLngs = [];
        let deliveryCount = 0;
        routePoints.forEach((point, index) => {
            // Skip points without valid coordinates (e.g., pending deliveries)
            if (!point.latitude || !point.longitude) return;

            const latlng = [parseFloat(point.latitude), parseFloat(point.longitude)];

            // Validate coordinates are numbers
            if (isNaN(latlng[0]) || isNaN(latlng[1])) return;

            latLngs.push(latlng);

            if (point.type === 'delivery') {
                deliveryCount++;
                const marker = L.marker(latlng, {
                    icon: L.divIcon({
                        className: 'custom-div-icon',
                        html: `<div style="background-color: ${point.point_id === selectedPointId ? '#f5222d' : '#1890ff'}; transform: ${point.point_id === selectedPointId ? 'scale(1.2)' : 'scale(1)'}; transition: all 0.3s; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; justify-content: center; align-items: center; border: 2px solid white; font-weight: bold; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${deliveryCount}</div>`,
                        iconSize: [24, 24],
                        iconAnchor: [12, 12]
                    })
                }).addTo(map);

                // Guardar referencia del marcador
                if (point.point_id) {
                    markersRef.current[point.point_id] = marker;
                }

                const popupContent = `
                    <div style="min-width: 250px; font-family: sans-serif;">
                    <h4 style="margin: 0 0 8px 0; color: #1890ff;">Entrega #${deliveryCount}</h4>
                    <p><strong>Pedido:</strong> ${point.order_number}</p>
                    <p><strong>Cliente:</strong> ${point.customer_name}</p>
                    <p><strong>Dirección:</strong> ${point.customer_address}</p>
                    <p><strong>Hora:</strong> ${new Date(point.timestamp).toLocaleTimeString()}</p>
                    ${point.payment_collected > 0 ? `<p><strong>Recaudado:</strong> <span style="color: #52c41a; font-weight: bold;">$${new Intl.NumberFormat('es-CO').format(point.payment_collected)}</span> (${point.collected_payment_method || 'N/A'})</p>` : ''}
                    ${point.photo_filename ? `<div style="margin-top: 10px;"><img src="/api/uploads/delivery_evidence/${point.photo_filename}" style="width: 100%; border-radius: 8px; border: 1px solid #eee;" /></div>` : ''}
                    </div>
                `;
                marker.bindPopup(popupContent);
            } else {
                // Punto de trayecto (path)
                L.circleMarker(latlng, {
                    radius: 4,
                    fillColor: '#1890ff',
                    color: '#fff',
                    weight: 1,
                    opacity: 1,
                    fillOpacity: 0.8
                }).addTo(map).bindPopup(`Trayecto: ${new Date(point.timestamp).toLocaleTimeString()}`);
            }
        });

        // Dibujar la ruta (línea que une todos los puntos cronológicamente)
        if (latLngs.length > 1) {
            L.polyline(latLngs, { color: '#1890ff', weight: 2, opacity: 0.5, dashArray: '5, 8' }).addTo(map);
        }

        // Ajustar vista para encuadrar todos los puntos
        if (latLngs.length > 0) {
            const bounds = L.latLngBounds(latLngs);
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [50, 50] });
            }
        }

    }, [L, routePoints, selectedPointId]);

    // Effect to handle selection without redrawing everything
    useEffect(() => {
        if (!L || !mapInstanceRef.current || !selectedPointId || !markersRef.current[selectedPointId]) return;

        const marker = markersRef.current[selectedPointId];
        const map = mapInstanceRef.current;

        marker.openPopup();

        // Optional: pan to marker
        // map.setView(marker.getLatLng(), 15);

    }, [L, selectedPointId]);

    return (
        <div
            ref={mapContainerRef}
            style={{
                height,
                width: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e8e8e8'
            }}
        />
    );
};

export default MessengerTrackingMap;
